import React, { useState, useEffect, useRef } from 'react'
import { Link, usePage } from '@inertiajs/react'
import useTranslation from '../hooks/useTranslation'

export default function UserLayout({ children }) {
  const { auth } = usePage().props
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isAdminDropdownOpen, setIsAdminDropdownOpen] = useState(false)
  const { t } = useTranslation()
  const formRef = useRef(null)
  
  useEffect(() => {
    // Add CSRF token to form when dropdown opens
    if (isDropdownOpen && formRef.current) {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      if (csrfToken) {
        // Remove existing token input if any
        const existingToken = formRef.current.querySelector('input[name="authenticity_token"]')
        if (existingToken) {
          existingToken.remove()
        }
        // Add new token input
        const tokenInput = document.createElement('input')
        tokenInput.type = 'hidden'
        tokenInput.name = 'authenticity_token'
        tokenInput.value = csrfToken
        formRef.current.appendChild(tokenInput)
      }
    }
  }, [isDropdownOpen])

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 font-sans text-gray-900 w-full">
      
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center gap-8">
              <Link href="/texts" className="flex items-center gap-2">
                 <div className="bg-emerald-600 text-white p-1.5 rounded-lg font-bold">LC</div>
                 <span className="font-bold text-xl tracking-tight text-emerald-900">LinguaClick</span>
              </Link>
              
              <div className="hidden md:flex space-x-4">
                <Link href="/texts" className="text-gray-600 hover:text-emerald-600 px-3 py-2 rounded-md font-medium text-sm transition-colors">{t('navigation.library')}</Link>
                <Link href="/dashboard" className="text-gray-600 hover:text-emerald-600 px-3 py-2 rounded-md font-medium text-sm transition-colors">{t('navigation.dashboard')}</Link>
                <a href="/blog" className="text-gray-600 hover:text-emerald-600 px-3 py-2 rounded-md font-medium text-sm transition-colors">Blog</a>
                {auth?.user?.role === "admin" && (
                  <div className="relative">
                    <button
                      onClick={() => setIsAdminDropdownOpen(!isAdminDropdownOpen)}
                      className="text-gray-600 hover:text-emerald-600 px-3 py-2 rounded-md font-medium text-sm transition-colors flex items-center gap-1"
                    >
                      {t('navigation.admin')}
                      <svg className={`w-4 h-4 transition-transform ${isAdminDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    {/* Admin Dropdown Backdrop */}
                    {isAdminDropdownOpen && (
                      <div 
                        className="fixed inset-0 z-40" 
                        onClick={() => setIsAdminDropdownOpen(false)}
                      ></div>
                    )}

                    {/* Admin Dropdown Menu */}
                    {isAdminDropdownOpen && (
                      <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 border border-gray-100 z-50">
                        <Link 
                          href="/admin/blog_posts" 
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => setIsAdminDropdownOpen(false)}
                        >
                          Manage Blog Posts
                        </Link>
                        <Link 
                          href="/admin/texts" 
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => setIsAdminDropdownOpen(false)}
                        >
                          Manage Texts
                        </Link>
                        <Link 
                          href="/admin/tags" 
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => setIsAdminDropdownOpen(false)}
                        >
                          Manage Tags
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="hidden md:flex items-center gap-2 text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                <span>{t('navigation.xp')}:</span>
                <span className="font-bold text-emerald-600">{auth?.user?.current_xp || 0}</span>
              </div>

              <div className="relative">
                <button 
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-900 focus:outline-none"
                >
                  <span className="hidden md:block font-medium text-sm">{auth?.user?.email_address}</span>
                  <div className="w-8 h-8 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center font-bold">
                    {auth?.user?.email_address?.[0].toUpperCase()}
                  </div>
                </button>
                
                {/* Dropdown Backdrop to close on click outside */}
                {isDropdownOpen && (
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setIsDropdownOpen(false)}
                  ></div>
                )}

                {/* Dropdown Menu */}
                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 border border-gray-100 z-50">
                    <Link href="/settings" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">{t('navigation.settings')}</Link>
                    {auth?.user?.role === "admin" && (
                      <>
                        <div className="border-t border-gray-100 my-1"></div>
                        <Link href="/admin/blog_posts" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Manage Blog Posts</Link>
                        <Link href="/admin/texts" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Manage Texts</Link>
                        <Link href="/admin/tags" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Manage Tags</Link>
                      </>
                    )}
                    <form 
                      ref={formRef}
                      action="/session" 
                      method="POST"
                      data-turbo="false"
                    >
                      <input type="hidden" name="_method" value="DELETE" />
                      <button type="submit" className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                        {t('navigation.sign_out')}
                      </button>
                    </form>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Flash Messages */}
      {usePage().props.flash?.success && (
        <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 mb-4 mx-auto max-w-7xl mt-4">
          <div className="flex">
            <div className="flex-shrink-0">
               <svg className="h-5 w-5 text-emerald-400" viewBox="0 0 20 20" fill="currentColor">
                 <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
               </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-emerald-700">{usePage().props.flash.success}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Main Content */}
      <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm text-gray-500">
              &copy; {new Date().getFullYear()} LinguaClick. {t('navigation.learn_by_reading')}
            </div>
            <div className="flex space-x-6 text-sm text-gray-500">
              <Link href="/texts" className="hover:text-emerald-600">{t('navigation.library')}</Link>
              <a href="/blog" className="hover:text-emerald-600">Blog</a>
              <Link href="/settings" className="hover:text-emerald-600">{t('navigation.settings')}</Link>
              <a href="/terms" className="hover:text-emerald-600">{t('navigation.terms')}</a>
              <a href="/privacy" className="hover:text-emerald-600">{t('navigation.privacy')}</a>
              <a href="/legal-notice" className="hover:text-emerald-600">{t('navigation.legal_notice')}</a>
              <a
                href="#"
                className="hover:text-emerald-600"
                onClick={(e) => {
                  e.preventDefault()
                  window?.LinguaClickCookieConsent?.open?.()
                }}
              >
                {t('navigation.cookie_settings')}
              </a>
            </div>
          </div>
        </div>
      </footer>

    </div>
  )
}

